import { useDispatch } from 'react-redux';

import FilesClient from 'src/clients/FilesClient';
import {
  alertSnackbar,
  completeProgress,
  resetProgressValue,
  setProgressValue,
} from 'src/store/ui/actions';
import { S3Utils } from 'src/utils';
import { API } from 'src/utils/AmplifyApiUtils';
import FileUtils from 'src/utils/FileUtils';

export const useProgressDownload = (
  getProgress: typeof FilesClient.getDownloadProgress = FilesClient.getDownloadProgress,
) => {
  const dispatch = useDispatch();

  /**
   * call api to get download job progress
   * if job is completed, download file
   * if job failed, show the failure reason and stop polling
   * if job is still running, set progress bar value and set timeout to call api again
   * @param downloadFileKey file key to download
   */
  const updateDownloadProgress = async (
    downloadFileKey: string,
    entityId?: string,
    channelId?: string,
    path?: string,
  ) => {
    const downloadProgress = await getProgress(downloadFileKey);

    if (
      downloadProgress.status === 'FAILED' ||
      downloadProgress.entityStatus === 'FAILED'
    ) {
      const reason = downloadProgress.additionalFields?.failureReason || '';
      console.error('[zip-download] Job failed', { downloadFileKey, reason });
      dispatch(completeProgress());
      dispatch(
        alertSnackbar({
          errorMessage:
            reason ||
            'Your download failed. Please try again or contact support.',
        }),
      );
      return;
    }
    if (
      downloadProgress.status === 'ENABLED' ||
      downloadProgress.entityStatus === 'ENABLED'
    ) {
      const zippedFileKey = downloadFileKey.split('/').slice(2).join('/');
      const bundleName = downloadProgress.fields?.bundleName || 'download';

      try {
        const signedBundleURL = (
          await S3Utils.getFile(zippedFileKey, {
            level: 'protected',
          })
        ).toString();

        dispatch(completeProgress());

        // Direct browser download — the browser's native download manager
        // streams to disk instead of loading the entire zip into JS heap,
        // which fails for large bundles (1 000+ files).
        FileUtils.triggerBrowserDownload(signedBundleURL);

        dispatch(
          alertSnackbar({
            successMessage: 'Your download has begun',
          }),
        );

        API.post('AppAPI', '/events', {
          body: {
            eventType: 'fileDownloaded',
            eventDescription: 'Downloaded',
            context: {
              ...(entityId && { id: entityId }),
              fileKey: zippedFileKey,
              name: bundleName,
              ...(channelId && { channelId }),
              ...(path && { path }),
            },
          },
        }).catch(() => {});
      } catch (err) {
        console.error(
          '[zip-download] Failed to initiate download after zip completed',
          { downloadFileKey, zippedFileKey, error: err },
        );
        dispatch(completeProgress());
        dispatch(
          alertSnackbar({
            errorMessage:
              'Your download failed. Please try again or contact support.',
          }),
        );
      }
    } else {
      // While the worker is still in its expansion phase, totalFiles is 0 —
      // avoid dividing by zero so the bar sits at 0% instead of flashing NaN.
      const totalFiles = downloadProgress.additionalFields?.totalFiles ?? 0;
      const downloadedFiles =
        downloadProgress.additionalFields?.downloadedFiles ?? 0;
      const pct =
        totalFiles > 0 ? Math.round((downloadedFiles / totalFiles) * 100) : 0;
      dispatch(setProgressValue(pct));
      setTimeout(() => {
        updateDownloadProgress(downloadFileKey, entityId, channelId, path);
      }, 500);
    }
  };

  const startZipDownload = (
    downloadFileKey: string,
    numFiles: number,
    entityId?: string,
    channelId?: string,
    path?: string,
  ) => {
    if (downloadFileKey === '') {
      return;
    }
    dispatch(resetProgressValue());
    dispatch(
      alertSnackbar(
        {
          progressMessage: `Zipping ${numFiles} file${numFiles > 1 ? 's' : ''}`,
        },
        true,
      ),
    );
    updateDownloadProgress(downloadFileKey, entityId, channelId, path);
  };

  return startZipDownload;
};
